import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../Context/AuthContext'
import DarkAuthInput from '../../Components/Inputs/DarkAuthInput'
import logo from '../../Assets/new-logo.png'
import { Check, X } from 'react-feather'

const SignupScreen = () => {
  const { createNewUser } = useAuth()
  const { unvalidateAccessCode, validAccessCode } = useAuth()
  const navigate = useNavigate()

  const [firstName, setFirstName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [verify, setVerify] = useState<string>('')
  const [phone, setPhone] = useState<string>('')
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showVerifyPassword, setShowVerifyPassword] = useState<boolean>(false)

  const [validEmail, setValidEmail] = useState<boolean>(false)
  const [validPassword, setValidPassword] = useState<boolean>(false)
  const [matchingVerify, setMatchingVerify] = useState<boolean>(false)
  const [validPhone, setValidPhone] = useState<boolean>(false)

  // Password requirements
  const [hasMinLength, setHasMinLength] = useState<boolean>(false)
  const [hasUpperCase, setHasUpperCase] = useState<boolean>(false)
  const [hasNumber, setHasNumber] = useState<boolean>(false)

  useEffect(() => {
    checkForValidAccessCode()
  }, [])

  const checkForValidAccessCode = () => {
    if (validAccessCode === false) {
      navigate('/auth/accessCode')
    }
  }

  const checkForValidPhone = (phoneNumber: string) => {
    const cleanedPhoneNumber = phoneNumber.replace(/\D/g, '')
    setValidPhone(cleanedPhoneNumber.length === 10)
  }

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFirstName(e.target.value)
  }

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(e.target.value)
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    checkForValidPhone(e.target.value)
    setPhone(e.target.value)
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    setValidEmail(emailRegex.test(e.target.value))
    setEmail(e.target.value)
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    const isValidLength = val.length >= 8
    const upperCase = /[A-Z]/.test(val)
    const number = /[0-9]/.test(val)

    setHasMinLength(isValidLength)
    setHasUpperCase(upperCase)
    setHasNumber(number)
    setValidPassword(isValidLength && upperCase && number)
    setMatchingVerify(val === verify && val.length > 0)
    setPassword(val)
  }

  const handleVerifyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMatchingVerify(e.target.value === password && e.target.value.length > 0)
    setVerify(e.target.value)
  }

  const handleSignup = (e: React.FormEvent) => {
    e.preventDefault()
    if (validAccessCode && validEmail && validPassword && validPhone && matchingVerify) {
      createNewUser({ firstName, lastName, email, password })
    }
  }

  const goToLogin = () => {
    unvalidateAccessCode()
    navigate('/')
  }

  const RequirementItem = ({ met, text }: { met: boolean; text: string }) => (
    <div className="flex items-center gap-2">
      {met ? (
        <Check className="w-3.5 h-3.5 text-green-400" />
      ) : (
        <X className="w-3.5 h-3.5 text-gray-500" />
      )}
      <span className={`text-xs ${met ? 'text-green-400' : 'text-gray-500'}`}>{text}</span>
    </div>
  )

  return (
    <div className="min-h-screen w-full bg-dark-bg flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background gradient orbs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-accent-purple/20 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2 animate-float" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-gold/10 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2 animate-float-slow" />
      <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-accent-pink/10 rounded-full blur-3xl animate-float-slower" />

      {/* Main card */}
      <div className="w-full max-w-md relative z-10 animate-fade-in-up">
        {/* Logo and header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-dark-card rounded-2xl border border-dark-border mb-4 animate-bounce-subtle">
            <img className="h-14 w-14 object-contain" src={logo} alt="Logo" />
          </div>
          <h1 className="text-3xl font-black text-white mb-2">Create Account</h1>
          <p className="text-gray-400">Join us and start managing your dashboard</p>
        </div>

        {/* Signup form card */}
        <div className="bg-dark-card border border-dark-border rounded-2xl p-8 shadow-2xl animate-scale-in">
          <form onSubmit={handleSignup}>
            {/* Name fields row */}
            <div className="grid grid-cols-2 gap-3">
              <DarkAuthInput
                value={firstName}
                handleFunction={handleFirstNameChange}
                placeholder="First name"
                type="text"
                icon="user"
              />
              <DarkAuthInput
                value={lastName}
                handleFunction={handleLastNameChange}
                placeholder="Last name"
                type="text"
                icon="user"
              />
            </div>

            <DarkAuthInput
              value={email}
              handleFunction={handleEmailChange}
              placeholder="Email address"
              type="text"
              icon="email"
              error={email && !validEmail ? 'Please enter a valid email' : undefined}
            />

            <DarkAuthInput
              value={password}
              handleFunction={handlePasswordChange}
              placeholder="Create password"
              type={showPassword ? 'text' : 'password'}
              icon="password"
              showPasswordToggle={true}
              onTogglePassword={() => setShowPassword(!showPassword)}
              isPasswordVisible={showPassword}
            />

            {/* Password requirements */}
            {password && (
              <div className="mb-4 p-3 bg-dark-input rounded-xl border border-dark-border">
                <p className="text-xs text-gray-400 mb-2">Password requirements:</p>
                <div className="space-y-1">
                  <RequirementItem met={hasMinLength} text="At least 8 characters" />
                  <RequirementItem met={hasUpperCase} text="One uppercase letter" />
                  <RequirementItem met={hasNumber} text="One number" />
                </div>
              </div>
            )}

            <DarkAuthInput
              value={verify}
              handleFunction={handleVerifyChange}
              placeholder="Confirm password"
              type={showVerifyPassword ? 'text' : 'password'}
              icon="password"
              showPasswordToggle={true}
              onTogglePassword={() => setShowVerifyPassword(!showVerifyPassword)}
              isPasswordVisible={showVerifyPassword}
              error={verify && !matchingVerify ? "Passwords don't match" : undefined}
            />

            {/* Divider */}
            <div className="flex items-center gap-4 my-4">
              <div className="flex-1 h-px bg-dark-border" />
              <span className="text-gray-500 text-xs">Contact Info</span>
              <div className="flex-1 h-px bg-dark-border" />
            </div>

            <DarkAuthInput
              value={phone}
              handleFunction={handlePhoneChange}
              placeholder="Phone number (000-000-0000)"
              type="tel"
              icon="phone"
              error={phone && !validPhone ? 'Enter a valid 10-digit phone number' : undefined}
            />

            <button
              type="submit"
              disabled={!validAccessCode || !validEmail || !validPassword || !validPhone || !matchingVerify}
              className="w-full py-3.5 px-4 bg-gradient-to-r from-accent-teal to-accent-deep-purple
                         text-white font-semibold rounded-xl
                         hover:opacity-90 hover:scale-[1.02] transition-all duration-200
                         disabled:opacity-50 disabled:cursor-not-allowed
                         shadow-lg shadow-accent-teal/25 hover:shadow-xl hover:shadow-accent-teal/30 mt-2"
            >
              Create Account
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-dark-border" />
            <span className="text-gray-500 text-sm">or</span>
            <div className="flex-1 h-px bg-dark-border" />
          </div>

          {/* Login link */}
          <p className="text-center text-gray-400 text-sm">
            Already have an account?{' '}
            <span
              onClick={goToLogin}
              className="text-accent-gold hover:text-accent-gold-light cursor-pointer font-medium transition-colors"
            >
              Sign In
            </span>
          </p>
        </div>

        {/* Security note */}
        <div className="mt-6 flex justify-center animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <div className="bg-dark-card/50 rounded-xl px-5 py-2.5 border border-dark-border hover:border-accent-teal/30 transition-colors duration-300">
            <p className="text-gray-500 text-xs text-center flex items-center gap-2">
              <svg className="w-4 h-4 text-accent-teal" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              Your data is secure and encrypted
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SignupScreen
